/**
 * チャット履歴の検索一致を CSS Custom Highlight API で塗る。
 *
 * DOM を書き換えず Range だけを登録するため、Markdown 描画済みの本文にも
 * 再レンダリングなしでハイライトを重ねられる。色は `::highlight()` 側 (globals.css) が持つ。
 *
 * 一致は本文欄の文字をつなげた上で探すため、装飾で分断された文字列 (`**Pod**man` の "Podman" など) も
 * 1 件として拾う。件数はデータ側 (transcript-search.ts) が同じ規則で数える。
 */

import { highlightRegistry } from "@/lib/highlight-registry";

/** 一致箇所のハイライト名。 */
const MATCH_HIGHLIGHT = "izanagi-transcript-match";

/** いま辿っている 1 箇所のハイライト名。 */
const ACTIVE_MATCH_HIGHLIGHT = "izanagi-transcript-match-active";

/** text 内で needle が始まる位置を、重なりなく先頭から返す。大文字小文字は区別しない。 */
export function findMatchOffsets(text: string, needle: string): number[] {
	if (needle.length === 0) return [];
	const lowered = text.toLowerCase();
	// 小文字化で長さが変わる文字 (İ など) があるとオフセットがずれるため、
	// そのときだけ大文字小文字を区別して探す。
	const haystack = lowered.length === text.length ? lowered : text;
	const offsets: number[] = [];
	for (let from = 0; ; ) {
		const index = haystack.indexOf(needle, from);
		if (index < 0) return offsets;
		offsets.push(index);
		from = index + needle.length;
	}
}

/** root 内の一致箇所を上から下へ返す。テキストノードをまたぐ一致も 1 つの Range にする。 */
export function collectMatchRanges(root: Element, query: string): Range[] {
	if (query.length === 0) return [];
	const nodes: Text[] = [];
	const starts: number[] = [];
	let text = "";
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
		nodes.push(node as Text);
		starts.push(text.length);
		text += (node as Text).data;
	}

	// 一致は昇順に来るため、ノードの探索位置は戻さない。
	let cursor = 0;
	const locate = (position: number, isEnd: boolean): [Text, number] => {
		const fits = (i: number) => {
			const end = starts[i] + nodes[i].length;
			return isEnd ? position <= end : position < end;
		};
		while (!fits(cursor)) cursor++;
		return [nodes[cursor], position - starts[cursor]];
	};

	const needle = query.toLowerCase();
	return findMatchOffsets(text, needle).map((offset) => {
		const range = document.createRange();
		range.setStart(...locate(offset, false));
		range.setEnd(...locate(offset + needle.length, true));
		return range;
	});
}

/** 登録済みのハイライトをすべて外す。 */
export function clearTranscriptHighlights(): void {
	const registry = highlightRegistry();
	if (registry === null) return;
	registry.delete(MATCH_HIGHLIGHT);
	registry.delete(ACTIVE_MATCH_HIGHLIGHT);
}

/** 一致箇所を塗り直す。active の 1 箇所だけ別色にする。 */
export function paintTranscriptMatches(others: readonly Range[], active: Range | undefined): void {
	const registry = highlightRegistry();
	if (registry === null) return;
	clearTranscriptHighlights();
	if (others.length > 0) registry.set(MATCH_HIGHLIGHT, new Highlight(...others));
	if (active !== undefined) registry.set(ACTIVE_MATCH_HIGHLIGHT, new Highlight(active));
}
